<template>
    <div>
        <div @mousedown="closeEditor($event)">
            <div class="px-xl-5 px-4">
                <div class="row">
                    <div class="col-12">
                        <br />
                        <br />
                        <div>
                            <button class="btn btn-link pl-0 py-1" @click="returnToEditor()">
                                <span class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1"></span>
                                <span>Back to Screen</span>
                            </button>
                        </div>
                        <br />
                        <br />
                    </div>
                </div>
                <!-- Header Description & Buttons -->
                <div class="row mb-4">
                    <div class="col-12">
                        <div
                            class="d-flex align-items-center mw-100"
                            :class="{
                                'hotspots-container-2d': !this.contentIs360,
                                'hotspots-container-360': this.contentIs360,
                            }"
                        >
                            <div class="flex-grow-1 mr-2 mr-md-5">
                                <p class="h6 font-weight-bolder">Configure Hotspots</p>
                                <p class="text-secondary small mb-0">
                                    Create a hotspot, then drag it to the desired position on the image. Click on a
                                    hotspot to edit its configuration.
                                </p>
                            </div>
                            <div class="flex-grow-0 flex-shrink-0">
                                <div class="nowrap" id="create-choice">
                                    <div class="mr-2 d-inline-block">
                                        <info-popover content="Show Editor Sidebar" placement="top">
                                            <button class="btn btn-gray btn-sm px-2" @click="toggleEditorDisplay()">
                                                <span class="icon icon-glyphicons-basic-sidebar-right"></span>
                                            </button>
                                        </info-popover>
                                    </div>
                                    <div class="mr-2 d-inline-block">
                                        <info-popover content="Add a hotspot" placement="top">
                                            <button
                                                @click="createChoice()"
                                                :disabled="editorLoading || readOnly"
                                                class="btn btn-gray btn-sm"
                                            >
                                                <span class="icon icon-glyphicons-basic-plus icon-mr"></span>
                                                <span>Hotspot</span>
                                                <span v-show="createLoading" class="ml-2">
                                                    <span
                                                        class="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    <span class="sr-only">Loading...</span>
                                                </span>
                                            </button>
                                        </info-popover>
                                    </div>
                                    <button
                                        @click="savePanel()"
                                        :disabled="editorLoading || readOnly"
                                        class="btn btn-primary btn-sm"
                                    >
                                        Save
                                        <span v-show="editorLoading" class="ml-2">
                                            <span
                                                class="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="sr-only">Loading...</span>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-4">
                    <div class="col-12">
                        <!-- 2D Hotspot Editor -->
                        <div
                            v-if="panel.hotspots_content_type == '2d_image'"
                            class="hotspots-container-2d image-container mw-100"
                        >
                            <div class="spots-container w-100 h-100" id="spots-container">
                                <div
                                    v-for="choice in panel.choices"
                                    :key="choice.id"
                                    @mousedown="selectChoice(choice, true, true, false)"
                                    @keydown.arrow-down.prevent="moveChoicePosition($event, 0, 2)"
                                    @keydown.arrow-up.prevent="moveChoicePosition($event, 0, -2)"
                                    @keydown.arrow-right.prevent="moveChoicePosition($event, 2, 0)"
                                    @keydown.arrow-left.prevent="moveChoicePosition($event, -2, 0)"
                                    v-show="!choice.is_deleted && showSpots"
                                    class="draggable"
                                    :id="'choice' + choice.id"
                                    :data-id="choice.id"
                                    :class="[
                                        choiceClass(choice),
                                        choice.id == selectedChoice.id ? 'spot-selected' : '',
                                        choice.marker_color == 'transparent' ? 'transparent-spot' : '',
                                        choice.marker_shape == 'circle' ? 'rounded-circle' : 'rounded',
                                    ]"
                                    :style="choiceStyle(choice)"
                                    tabindex="0"
                                ></div>
                            </div>
                            <div>
                                <img id="hotspots-image" :src="storedImageSource" class="w-100 rounded-sm" />
                            </div>
                        </div>
                        <!-- 360 Degree Hotspot Editor -->
                        <div
                            v-if="panel && panel.hotspots_content_type == '360_image'"
                            class="hotspots-container-360 mw-100"
                        >
                            <div class="card card-360 h-100">
                                <div class="card-body p-0">
                                    <hotspots-360
                                        :edit-mode="true"
                                        :panel="panel"
                                        :media-src="storedImageSource"
                                        :choices.sync="panel.choices"
                                        :show-prompts="false"
                                        v-on:select-choice="selectChoice($event, false)"
                                        v-on:open-menu="openEditor()"
                                        ref="hotspotEditor360"
                                    ></hotspots-360>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row" v-show="contentIs360">
                    <div class="col-12">
                        <div class="d-flex align-items-center">
                            <button @click="setInitialCameraAngle()" class="btn btn-sm btn-gray mr-2">
                                <span class="icon icon-glyphicons-basic-rotate-horizontal mr-1"></span>
                                Set Initial Camera Angle
                            </button>
                            <div class="small text-secondary" v-show="intialCameraAnglesSet">
                                ({{ convertRadsToDegrees(panel.init_camera_angle_alpha) }}&deg;,
                                {{ convertRadsToDegrees(panel.init_camera_angle_beta) }}&deg;)
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Editor Menu -->
            <div
                id="editor-menu"
                class="right-sidebar bg-white shadow border border-left border-gray"
                :class="{ 'd-none': !showEditor }"
            >
                <div class="right-sidebar-contentflex-column">
                    <div class="right-sidebar-content-block">
                        <div class="d-flex align-items-start left-sidebar-content-block mb-4">
                            <div class="flex-grow-1 pr-2">
                                <p class="text-secondary small mb-0">
                                    Click on a hotspot to highlight its marker on the image. Customize each hotspot's
                                    behaviour in the list below.
                                </p>
                            </div>
                            <div class="flex-grow-0">
                                <div @click="closeEditor()">
                                    <button type="button" class="close" aria-label="Close">
                                        <span
                                            class="icon icon-glyphicons-basic-menu-close align-middle"
                                            style="font-size: 20px"
                                        ></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-for="(choice, index) in panel.choices" :key="choice.id">
                            <div
                                @click="selectChoiceFromMenu(choice, $event)"
                                v-show="!choice.is_deleted"
                                :class="{ 'choice-focussed': choice.id === selectedChoice.id }"
                                class="card mb-3 bg-light"
                                :id="'edit-choice' + choice.id"
                            >
                                <div class="card-body px-0">
                                    <div class="d-flex">
                                        <div class="flex-grow-1">
                                            <!-- Choice Configuration -->
                                            <div class="d-flex mb-1">
                                                <div class="col-12">
                                                    <div class="d-flex">
                                                        <div class="flex-grow-1">
                                                            <div class="mb-2 pb-1">
                                                                <div
                                                                    class="custom-control custom-radio custom-control-inline"
                                                                >
                                                                    <input
                                                                        v-model="choice.marker_visible"
                                                                        :value="true"
                                                                        type="radio"
                                                                        :id="'visible-marker-true' + choice.id"
                                                                        class="custom-control-input"
                                                                    />
                                                                    <label
                                                                        :for="'visible-marker-true' + choice.id"
                                                                        class="custom-control-label"
                                                                        >Always visible</label
                                                                    >
                                                                </div>
                                                                <div
                                                                    class="custom-control custom-radio custom-control-inline"
                                                                >
                                                                    <input
                                                                        v-model="choice.marker_visible"
                                                                        :value="false"
                                                                        type="radio"
                                                                        :id="'visible-marker-false' + choice.id"
                                                                        class="custom-control-input"
                                                                    />
                                                                    <label
                                                                        :for="'visible-marker-false' + choice.id"
                                                                        class="custom-control-label"
                                                                        >Visible on hover</label
                                                                    >
                                                                </div>
                                                            </div>
                                                            <div
                                                                v-show="!contentIs360"
                                                                class="custom-control custom-checkbox mb-2"
                                                            >
                                                                <input
                                                                    v-model="choice.show_text_on_hover"
                                                                    type="checkbox"
                                                                    class="custom-control-input"
                                                                    :id="'show-text' + choice.id"
                                                                />
                                                                <label
                                                                    class="custom-control-label"
                                                                    :for="'show-text' + choice.id"
                                                                    >Show label on hover</label
                                                                >
                                                            </div>
                                                        </div>
                                                        <div class="flex-grow-0">
                                                            <div
                                                                class="mr-2 d-flex align-items-center justify-content-end"
                                                            >
                                                                <div>
                                                                    <span
                                                                        v-show="choice.id === selectedChoice.id"
                                                                        class="badge badge-primary align-top"
                                                                        >Selected</span
                                                                    >
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Choice Text -->
                                            <div>
                                                <div class="d-flex mb-2 pb-1">
                                                    <div class="col-12">
                                                        <div class="mb-1 text-secondary">Label</div>
                                                        <div>
                                                            <input
                                                                v-model="choice.text"
                                                                placeholder="Hotspot Label"
                                                                class="form-control"
                                                                maxlength="200"
                                                                rows="2"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Actions -->
                                            <div class="mb-1 d-flex">
                                                <div class="col-6 pr-2">
                                                    <div class="mb-1 text-secondary">Click Action</div>
                                                    <select v-model="choice.action" class="form-control">
                                                        <option value="none">None</option>
                                                        <option value="show_content_modal">Show a Pop-Up</option>
                                                        <option value="proceed_to_node">Proceed to Screen</option>
                                                    </select>
                                                </div>
                                                <div v-show="choice.action === 'proceed_to_node'" class="col-6 pl-1">
                                                    <div class="mb-1 text-secondary">Target Screen</div>
                                                    <node-select
                                                        v-model="choice.target_node"
                                                        :node-options="nodeOptions"
                                                        placeholder="Select or create a screen"
                                                        ref="nodeSelector"
                                                    ></node-select>
                                                </div>
                                                <div v-show="choice.action === 'show_content_modal'" class="col-6 pl-1">
                                                    <div class="mb-1 text-secondary">Pop-Up Width</div>
                                                    <select
                                                        v-model="panel.choices[index].modal_width"
                                                        class="form-control"
                                                    >
                                                        <option
                                                            v-for="option in modalWidthOptions"
                                                            :key="option.value"
                                                            :value="option.value"
                                                        >
                                                            {{ option.label }}
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>

                                            <!-- Content Modal -->
                                            <div v-show="choice.action === 'show_content_modal'">
                                                <div class="d-flex mb-3">
                                                    <div class="col-12">
                                                        <a
                                                            class="badge badge-gray"
                                                            @click="openChoiceElementEditor(choice)"
                                                        >
                                                            <span
                                                                class="icon icon-glyphicons-basic-pencil icon-mr"
                                                            ></span>
                                                            <span>Edit Content</span>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Score & Correctness -->
                                            <div v-show="choice.action !== 'none'">
                                                <div class="d-flex mb-3">
                                                    <div class="col-6 pr-2">
                                                        <div class="mb-1 text-secondary">Score</div>
                                                        <input
                                                            v-model="choice.score"
                                                            class="form-control"
                                                            type="number"
                                                        />
                                                    </div>
                                                    <div class="col-12 col-sm-6 pl-sm-1">
                                                        <div class="mb-1 text-secondary">Correct/Incorrect</div>
                                                        <select v-model="choice.correct" class="form-control">
                                                            <option :value="true">Correct</option>
                                                            <option :value="false">Incorrect</option>
                                                            <option :value="null">Neutral</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Radius & Color -->
                                            <div class="d-flex">
                                                <div class="col-12">
                                                    <p class="text-secondary mb-1">Marker Size & Color</p>
                                                </div>
                                            </div>
                                            <div class="d-flex align-items-center">
                                                <div class="col-12">
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0 mr-3">
                                                            <div class="d-flex align-items-center">
                                                                <div class="dropdown mr-3" v-show="!contentIs360">
                                                                    <button
                                                                        class="btn btn-white btn-shape-picker"
                                                                        type="button"
                                                                        data-toggle="dropdown"
                                                                        aria-haspopup="true"
                                                                        aria-expanded="false"
                                                                    >
                                                                        <div class="d-flex align-items-center">
                                                                            <span
                                                                                class="icon icon-lg text-secondary"
                                                                                :class="{
                                                                                    'icon-glyphicons-basic-circle-empty':
                                                                                        choice.marker_shape == 'circle',
                                                                                    'icon-glyphicons-basic-square-empty':
                                                                                        choice.marker_shape ==
                                                                                        'rectangle',
                                                                                }"
                                                                            ></span>
                                                                            <span
                                                                                class="icon icon-xs icon-glyphicons-basic-chevron-down text-secondary"
                                                                            ></span>
                                                                        </div>
                                                                    </button>
                                                                    <div class="dropdown-menu shape-dropdown">
                                                                        <a class="dropdown-item px-3">
                                                                            <span
                                                                                class="icon icon-xl icon-glyphicons-basic-circle-empty text-secondary"
                                                                                @click="
                                                                                    setChoiceShape(choice, 'circle')
                                                                                "
                                                                            ></span>
                                                                        </a>
                                                                        <a class="dropdown-item px-3">
                                                                            <span
                                                                                class="icon icon-xl icon-glyphicons-basic-square-empty text-secondary"
                                                                                @click="
                                                                                    setChoiceShape(choice, 'rectangle')
                                                                                "
                                                                            ></span>
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                                <div class="radius-range pt-2">
                                                                    <input
                                                                        v-model="choice.marker_radius"
                                                                        type="range"
                                                                        step="1"
                                                                        min="5"
                                                                        :max="maxMarkerRadius"
                                                                        class="custom-range"
                                                                        @change="updateRadius(choice)"
                                                                    />
                                                                </div>
                                                                <div class="mr-2">
                                                                    <input
                                                                        v-model="choice.marker_radius"
                                                                        type="number"
                                                                        class="form-control form-control-sm radius-range-input ml-3 p-1 border-gray"
                                                                        @change="updateRadius(choice)"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="flex-grow-0 d-flex align-items-center">
                                                            <div class="dropdown">
                                                                <hotspot-color-picker
                                                                    v-if="choice.marker_color"
                                                                    v-model="choice.marker_color"
                                                                ></hotspot-color-picker>
                                                            </div>
                                                            <div class="dropdown" v-show="!contentIs360">
                                                                <button
                                                                    class="p-1 btn btn-sm btn-white"
                                                                    type="button"
                                                                    data-toggle="dropdown"
                                                                    aria-haspopup="true"
                                                                    aria-expanded="false"
                                                                >
                                                                    <div class="d-flex align-items-center">
                                                                        <span class="mr-1">{{
                                                                            choice.marker_style == "solid"
                                                                                ? "Solid"
                                                                                : "Outline"
                                                                        }}</span>
                                                                        <span
                                                                            class="icon icon-xs icon-glyphicons-basic-chevron-down text-secondary"
                                                                        ></span>
                                                                    </div>
                                                                </button>
                                                                <div class="dropdown-menu style-dropdown">
                                                                    <a
                                                                        class="dropdown-item px-3 small"
                                                                        @click="setChoiceMarkerStyle(choice, 'solid')"
                                                                    >
                                                                        Solid
                                                                    </a>
                                                                    <a
                                                                        class="dropdown-item px-3 small"
                                                                        @click="setChoiceMarkerStyle(choice, 'outline')"
                                                                    >
                                                                        Outline
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="mt-3 d-flex align-items-center">
                                                        <div
                                                            class="custom-control custom-checkbox mr-3"
                                                            style="padding-top: 5px; padding-bottom: 5px"
                                                        >
                                                            <input
                                                                v-model="choice.change_color_when_explored"
                                                                type="checkbox"
                                                                class="custom-control-input"
                                                                :id="'change-color' + choice.id"
                                                            />
                                                            <label
                                                                class="custom-control-label"
                                                                :for="'change-color' + choice.id"
                                                                >Change color when explored</label
                                                            >
                                                        </div>
                                                        <hotspot-color-picker
                                                            v-if="choice.change_color_when_explored"
                                                            v-model="choice.marker_color_after_explored"
                                                        ></hotspot-color-picker>
                                                    </div>
                                                    <div v-if="contentIs360" class="mt-2">
                                                        <!-- Hotspot Angle -->
                                                        <div class="pt-1">
                                                            <div class="d-flex align-items-center">
                                                                <div class="mr-2">
                                                                    <span
                                                                        class="icon icon-glyphicons-basic-rotate-horizontal"
                                                                    ></span>
                                                                </div>
                                                                <div class="radius-range pt-2 mr-2">
                                                                    <input
                                                                        v-model="choice.rotation_y"
                                                                        type="range"
                                                                        step="1"
                                                                        min="0"
                                                                        max="180"
                                                                        class="custom-range"
                                                                        @change="updateAngle(choice)"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <span class="text-secondary small"
                                                                        >{{ choice.rotation_y }}&deg;</span
                                                                    >
                                                                </div>
                                                            </div>
                                                            <div class="d-flex align-items-center">
                                                                <div class="mr-2">
                                                                    <span
                                                                        class="icon icon-glyphicons-basic-rotate-vertical"
                                                                    ></span>
                                                                </div>
                                                                <div class="radius-range pt-2 mr-2">
                                                                    <input
                                                                        v-model="choice.rotation_x"
                                                                        type="range"
                                                                        step="1"
                                                                        min="0"
                                                                        max="180"
                                                                        class="custom-range"
                                                                        @change="updateAngle(choice)"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <span class="text-secondary small"
                                                                        >{{ choice.rotation_x }}&deg;</span
                                                                    >
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="d-flex align-items-center justify-content-end mt-1">
                                                        <div>
                                                            <button
                                                                @click="cloneChoice(choice)"
                                                                class="btn btn-sm btn-transparent"
                                                                data-function="clone-btn"
                                                            >
                                                                <span class="icon icon-glyphicons-basic-squares"></span>
                                                                Clone
                                                                <span
                                                                    v-show="cloneLoadingId === choice.id"
                                                                    class="ml-1"
                                                                >
                                                                    <span
                                                                        class="spinner-border spinner-border-sm"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                    ></span>
                                                                    <span class="sr-only">Loading...</span>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <button
                                                                @click="removeChoice(choice)"
                                                                class="btn btn-sm btn-transparent"
                                                            >
                                                                <span
                                                                    class="icon icon-glyphicons-basic-bin icon-btn"
                                                                ></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-show="panel.choices.length == 0">
                            <p class="text-center text-secondary my-4">No hotspots created yet.</p>
                        </div>
                        <div class="d-flex justify-content-end">
                            <button @click="createChoice()" class="btn btn-gray btn-sm mr-2">
                                <span class="icon icon-glyphicons-basic-plus icon-mr"></span>
                                <span>Hotspot</span>
                            </button>
                            <button @click="savePanel()" :disabled="editorLoading" class="btn btn-primary btn-sm">
                                Save
                                <span v-show="editorLoading" class="ml-2">
                                    <span
                                        class="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    <span class="sr-only">Loading...</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Choice Modal Content Editor -->
        <base-modal id="content-editor-modal" :loading="modalLoading" :centered="true" size="large">
            <template slot="title"> Content Editor </template>
            <template>
                <rich-content-editor
                    v-model="selectedChoice.elements"
                    :allowed-video-fields="['autoplay_video', 'loop_media', 'hide_video_controls']"
                    ref="richContentEditor"
                ></rich-content-editor>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button class="btn btn-primary" type="button" :disabled="modalLoading" @click="saveChoiceElements()">
                    Add
                </button>
            </template>
        </base-modal>
        <global-style-block v-if="caseObj" :case-settings="caseObj"></global-style-block>
    </div>
</template>

<script>
import http from "@/axios.js"
import interact from "interactjs"
import RichContentEditor from "@/components/RichContentEditor"
import BaseModal from "@/components/BaseModal"
import GlobalStyleBlock from "@/components/GlobalStyleBlock"
import NodeSelect from "@/components/NodeSelect"
import InfoPopover from "@/components/InfoPopover"
import HotspotColorPicker from "@/components/HotspotColorPicker"
import { hotspotColors } from "@/assets/js/settings.js"
import Utilities from "@/assets/js/utilities.js";

const degreesToRadians = (2 * Math.PI) / 360

export default {
    name: "ImageHotspotsEditor",
    components: {
        BaseModal,
        RichContentEditor,
        GlobalStyleBlock,
        NodeSelect,
        InfoPopover,
        HotspotColorPicker,
        Hotspots360: () => import("@/components/Hotspots360"),
    },
    data() {
        return {
            panel: {
                elements: [],
                choices: [],
            },
            selectedChoice: {
                elements: [],
            },
            showEditor: true,
            showSpots: false,
            markerColors: hotspotColors,
            editorLoading: false,
            modalLoading: false,
            createLoading: false,
            cloneLoadingId: null,
            modalWidthOptions: [
                {
                    label: "Small",
                    value: "medium",
                },
                {
                    label: "Medium",
                    value: "large",
                },
                {
                    label: "Large",
                    value: "extra_large",
                },
            ],
            previousRoute: null,
            // The source (url) of the image, stored to avoid unecessary reloads of the
            // image after it's been loaded the first time.
            storedImageSource: null,
        }
    },
    computed: {
        caseObj() {
            return this.$store.getters.case
        },
        caseID() {
            return this.$route.params.caseID
        },
        nodeID() {
            return this.$route.params.nodeID
        },
        panelID() {
            return this.$route.params.panelID
        },
        readOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        imageSource() {
            if (!this.panel.elements[0]) {
                return null
            }
            return this.panel.elements[0].stored_file.signed_url
        },
        nodeOptions() {
            return this.$store.getters.nodeOptions
        },
        activeChoices() {
            return this.panel.choices.filter((c) => !c.is_deleted)
        },
        contentIs360() {
            return this.panel.hotspots_content_type == "360_image"
        },
        maxMarkerRadius() {
            return this.contentIs360 ? 80 : 400
        },
        intialCameraAnglesSet() {
            return this.panel.init_camera_angle_alpha !== null && this.panel.init_camera_angle_beta !== null
        },
        returnURL() {
            return `/library/${this.caseID}/nodes/${this.nodeID}/`
        },
    },
    methods: {
        // Fetch initial data for the view.
        fetchData() {
            return Promise.all([this.fetchPanel(), this.fetchCase()])
        },
        // Fetch details for the panel.
        fetchPanel() {
            let vm = this
            this.$store.dispatch("addLoad")
            return http
                .get("/api/panels/" + this.panelID + "/")
                .then(function (response) {
                    vm.panel = response.data
                    vm.$nextTick(function () {
                        vm.initHotspotPositions()
                    })
                    if (!vm.storedImageSource) {
                        vm.storedImageSource = vm.imageSource
                    }
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        // Fetch details about the current case being viewed.
        fetchCase() {
            let vm = this
            this.$store.dispatch("addLoad")
            return this.$store.dispatch("fetchCase", this.caseID).then(function () {
                vm.$store.dispatch("removeLoad")
            })
        },
        // Initialize hotspots positions for 2D images.
        async initHotspotPositions() {
            if (this.panel.hotspots_content_type == "2d_image") {
                await this.init2DHotspots()
                this.initializeInteract()
            }
        },
        // Initialize the position and sizing of the 2D hotspots, using the size of the image.
        async init2DHotspots() {
            var img = document.getElementById("hotspots-image")
            let vm = this

            function loaded() {
                // Fetch the width and height of the image.
                let image = document.getElementById("hotspots-image")
                let imageWidth = parseFloat(image.offsetWidth)
                let imageHeight = parseFloat(image.offsetHeight)

                vm.showSpots = true

                // Format the x and y position of each choice.
                vm.panel.choices.forEach((choice) => {
                    vm.format2DHotspotsForDisplay(choice, imageWidth, imageHeight)
                })

                vm.$nextTick(function () {
                    return Promise.resolve()
                })
            }

            if (img.complete) {
                loaded()
            } else {
                img.addEventListener("load", loaded)
            }
        },
        // Initialize 3D hotspots.
        init3DHotspots() {
            this.$refs.hotspotEditor360.addHotspotsToScene()
        },
        // Format a choice object as a 2D hotspot for display in the editor.
        format2DHotspotsForDisplay(choice, imageWidth, imageHeight) {
            if (!imageHeight || !imageWidth) {
                // Fetch the width and height of the image.
                let image = document.getElementById("hotspots-image")
                imageWidth = parseFloat(image.offsetWidth)
                imageHeight = parseFloat(image.offsetHeight)
            }

            // Ensure that x and y positions don't exceed 100% of the image width & height).
            let choicePositionX = choice.position_x > 1 ? 1 : choice.position_x
            choicePositionX = choice.position_x < 0 ? 0 : choicePositionX
            let choicePositionY = choice.position_y > 1 ? 1 : choice.position_y
            choicePositionY = choice.position_y < 0 ? 0 : choicePositionY

            let xPosition = imageWidth * choicePositionX
            let yPosition = imageHeight * choicePositionY

            // Ensure that the marker radius doesn't exceed 100% of image width.
            let choiceMarkerRad = choice.marker_radius > 1 ? 1 : choice.marker_radius
            let radius = imageWidth * choiceMarkerRad
            choice.marker_radius = Math.round(radius * 100) / 100

            let height = imageHeight * choice.marker_height
            choice.marker_height = Math.round(height * 100) / 100

            let markerElement = document.getElementById("choice" + choice.id)
            markerElement.style.transform = `translate(${xPosition}px, ${yPosition}px)`
            markerElement.setAttribute("data-x", xPosition)
            markerElement.setAttribute("data-y", yPosition)
        },
        // Format a choice object as a 2D hotspot for saving.
        format2DHotspotForSave(choice, imageWidth = null, imageHeight = null, isCreated = true) {
            if (!imageHeight || !imageWidth) {
                // Fetch the width and height of the image.
                let image = document.getElementById("hotspots-image")
                imageWidth = parseFloat(image.offsetWidth)
                imageHeight = parseFloat(image.offsetHeight)
            }

            let xPosition = 0
            let yPosition = 0
            if (isCreated) {
                xPosition = document.getElementById("choice" + choice.id).getAttribute("data-x")
                yPosition = document.getElementById("choice" + choice.id).getAttribute("data-y")
            }

            let xPositionPercentage = parseFloat(xPosition / imageWidth)
            let yPositionPercentage = parseFloat(yPosition / imageHeight)
            xPositionPercentage = Math.round(1000 * xPositionPercentage) / 1000
            yPositionPercentage = Math.round(1000 * yPositionPercentage) / 1000

            let radiusPercentage = parseFloat(choice.marker_radius / imageWidth)
            radiusPercentage = Math.round(1000 * radiusPercentage) / 1000

            let heightPercentage = parseFloat(choice.marker_height / imageHeight)
            heightPercentage = Math.round(1000 * heightPercentage) / 1000

            let choiceFormatted = { ...choice }
            choiceFormatted.position_x = xPositionPercentage
            choiceFormatted.position_y = yPositionPercentage
            choiceFormatted.marker_radius = radiusPercentage
            choiceFormatted.marker_height = heightPercentage

            return choiceFormatted
        },
        // Initialize the interact library for the 2D drag and drop hotspots editor.
        initializeInteract(selector = ".draggable") {
            let vm = this
            // Target elements with the "draggable" class.
            interact(selector)
                .resizable({
                    // Resize from all edges and corners.
                    edges: { left: true, right: true, bottom: true, top: true },
                    margin: 10,
                    listeners: {
                        move(event) {
                            let target = event.target
                            let x = parseFloat(target.getAttribute("data-x")) || 0
                            let y = parseFloat(target.getAttribute("data-y")) || 0

                            // Fetch the associated choice element.
                            let choiceId = event.target.getAttribute("data-id")
                            let choice = vm.panel.choices.find((c) => c.id == choiceId)

                            let newWidth = Math.round(event.rect.width)
                            let newHeight = Math.round(event.rect.height)

                            if (choice.marker_shape == "circle") {
                                // For circles, ensure a perfect circle (height = width).
                                if (event.edges.left || event.edges.right) {
                                    choice.marker_radius = newWidth
                                } else {
                                    choice.marker_radius = newHeight
                                }
                            } else {
                                choice.marker_radius = newWidth
                                choice.marker_height = newHeight
                            }

                            // Translate when resizing from top or left edges.
                            x += event.deltaRect.left
                            y += event.deltaRect.top

                            target.style.transform = "translate(" + x + "px," + y + "px)"

                            target.setAttribute("data-x", x)
                            target.setAttribute("data-y", y)
                        },
                    },
                    modifiers: [
                        // Keep the edges inside the parent.
                        interact.modifiers.restrictEdges({
                            outer: "parent",
                        }),

                        // Minimum size.
                        interact.modifiers.restrictSize({
                            min: { width: 10, height: 10 },
                        }),
                    ],
                    inertia: true,
                })
                .draggable({
                    // Enable inertial throwing.
                    inertia: true,
                    // Keep the element within the area of it's parent.
                    modifiers: [
                        interact.modifiers.restrictRect({
                            restriction: "parent",
                            endOnly: true,
                        }),
                    ],
                    // Enable autoScroll.
                    autoScroll: true,
                    listeners: {
                        // Call this function on every dragmove event.
                        move: dragMoveListener,

                        // Call this function on every dragend event.
                        end() {},
                    },
                })

            function dragMoveListener(event) {
                var target = event.target

                // Keep the dragged position in the data-x/data-y attributes.
                var x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx
                var y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy

                // Translate the element.
                target.style.transform = "translate(" + x + "px, " + y + "px)"

                // Update the position attributes.
                target.setAttribute("data-x", x)
                target.setAttribute("data-y", y)
            }
        },
        // Replace the panel that's currently being edited with a fresh object.
        updatePanel(panel) {
            this.panel = panel
            if (this.contentIs360) {
                // Wait for the change to propagate to the child component, then reset
                // the 360 hotspots.
                let vm = this
                this.$nextTick(function () {
                    vm.$refs.hotspotEditor360.resetHotspots()
                })
            }
        },
        // Save the panel and choice (hotspot) configuration.
        savePanel() {
            this.editorLoading = true
            let vm = this

            // Set any blank scores to zero.
            this.panel.choices.filter((choice) => !choice.score).forEach((choice) => (choice.score = 0))

            // Fetch the x and y positions of each choice.
            let choices = []
            if (this.contentIs360) {
                // Save the hotspot positions and angles in 360 space.
                this.panel.choices.forEach((choice) => {
                    let choiceFormatted = {}
                    // Add all the choice fields to the
                    for (const [key, value] of Object.entries(choice)) {
                        if (key !== "disc") {
                            choiceFormatted[key] = value
                        }
                    }
                    choiceFormatted.rotation_x = choice.disc.rotation.x / degreesToRadians
                    choiceFormatted.rotation_y = choice.disc.rotation.y / degreesToRadians
                    choiceFormatted.rotation_z = choice.disc.rotation.z / degreesToRadians
                    choiceFormatted.position_x = choice.disc.position.x
                    choiceFormatted.position_y = choice.disc.position.y
                    choices.push(choiceFormatted)
                })
            } else {
                // Fetch the width and height of the image.
                let image = document.getElementById("hotspots-image")
                let imageWidth = parseFloat(image.offsetWidth)
                let imageHeight = parseFloat(image.offsetHeight)

                // Format the hotspot positions as percentages of the containing image.
                this.panel.choices.forEach((choice) => {
                    let choiceFormatted = vm.format2DHotspotForSave(choice, imageWidth, imageHeight)
                    choices.push(choiceFormatted)
                })
            }

            let data = { ...this.panel }
            data.choices = choices

            http.put("/api/panels/" + this.panel.id + "/", data)
                .then(function (response) {
                    vm.updatePanel(response.data)
                    vm.initHotspotPositions()
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Hotspots saved.",
                        data: {},
                    })
                    vm.$store.commit("mapRequiresUpdate")
                    vm.$store.commit("selectedNodeRequiresUpdate")
                    // Do a refresh to update the list of available nodes.
                    vm.$store.dispatch("refreshCase", vm.caseID)
                })
                .finally(function () {
                    vm.editorLoading = false
                })
        },
        // Create and persist a new choice.
        async createChoice() {
            this.createLoading = true
            let index = this.panel.choices.length
            let newChoiceRadius = this.contentIs360 ? 12 : 60
            let choice = {
                text: "",
                action: "show_content_modal",
                message: "",
                target_node: null,
                correct: null,
                marker_radius: newChoiceRadius,
                marker_height: newChoiceRadius,
                marker_color: "white",
                marker_visible: true,
                position_x: 0,
                position_y: 0,
                position_z: 0,
                rotation_x: 0,
                rotation_y: 0,
                rotation_z: 0,
                order: index,
            }

            let choiceFormatted = choice
            if (!this.contentIs360) {
                choiceFormatted = this.format2DHotspotForSave(choice, null, null, false)
            }

            let vm = this
            let response = await http
                .post(`/api/panels/${this.panelID}/choices/`, choiceFormatted)
                .finally(() => (vm.createLoading = false))
            let newChoice = response.data

            // Add the choice to the panel.
            this.panel.choices.splice(index, 0, newChoice)

            // Select the choice in the menu after the next render.
            this.$nextTick(function () {
                this.selectChoice(newChoice)
            })

            // Initialize the interactive hotspot, depending on 2D vs. 3D.
            if (this.contentIs360) {
                this.$refs.hotspotEditor360.addHotspot(newChoice, true)
            } else {
                this.$nextTick(function () {
                    vm.format2DHotspotsForDisplay(newChoice)
                    vm.initializeInteract("#choice" + newChoice.id)
                })
            }
        },
        // Clone and persist an existing choice.
        async cloneChoice(choice) {
            this.cloneLoadingId = choice.id

            // Clone the choice object server side. Need to do this to capture
            // any elements that are saved on the choice.
            let vm = this
            let response = await http.post(`/api/choices/${choice.id}/clone/`).finally(() => (vm.cloneLoadingId = null))
            let newChoice = response.data

            // Set each choice attribute to match the choice that the user is seeing,
            // what's on screen may be different than what's saved.
            let choiceSaveFormatted = choice
            if (!this.contentIs360) {
                choiceSaveFormatted = this.format2DHotspotForSave(choice)
            }
            let cloneAttribs = [
                "text",
                "action",
                "message",
                "target_node",
                "correct",
                "marker_radius",
                "marker_height",
                "marker_shape",
                "marker_color",
                "marker_visible",
                "marker_style",
                "show_text_on_hover",
                "position_x",
                "position_y",
                "position_z",
                "rotation_x",
                "rotation_y",
                "rotation_z",
            ]
            cloneAttribs.forEach((attrib) => {
                if (attrib == "position_x" || attrib == "position_y") {
                    // Move the hotspot slightly beside the original, to make it more
                    // obvious that it's been cloned.
                    newChoice[attrib] = choiceSaveFormatted[attrib] + 0.01
                } else {
                    newChoice[attrib] = choiceSaveFormatted[attrib]
                }
            })

            // Add the choice to the panel.
            let index = this.panel.choices.length
            this.panel.choices.splice(index, 0, newChoice)

            // Select the choice in the menu after the next render.
            this.$nextTick(function () {
                this.selectChoice(newChoice)
            })

            // Initialize the interactive hotspot, depending on 2D vs. 3D.
            if (this.contentIs360) {
                this.$refs.hotspotEditor360.addHotspot(newChoice, true)
            } else {
                this.$nextTick(function () {
                    this.format2DHotspotsForDisplay(newChoice)
                    this.initializeInteract("#choice" + newChoice.id)
                })
            }
        },
        // Set the deleted flag to true for a choice.
        removeChoice(choice) {
            this.$set(choice, "is_deleted", true)
            if (this.contentIs360) {
                this.$refs.hotspotEditor360.removeHotspot(choice)
            }
        },
        // Select a choice, and optionally open the editor sidebar.
        selectChoice(choice, showEditor = true, scrollToItem = true, focusChoice = false) {
            this.selectedChoice = choice
            if (showEditor) {
                this.showEditor = true
            }
            if (focusChoice) {
                document.getElementById("choice" + choice.id).focus()
            }
            if (scrollToItem) {
                this.$nextTick(function () {
                    let choiceElement = document.getElementById("edit-choice" + choice.id)
                    choiceElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    })
                })
            }
        },
        // Select a choice by clicking on it in the editor sidebar.
        selectChoiceFromMenu(choice, event) {
            // Don't do anything if the clone button was clicked.
            let clickedInClone = Boolean(event.target.closest("[data-function='clone-btn']"))
            if (clickedInClone) {
                return
            }
            if (this.selectedChoice.id !== choice.id && this.contentIs360) {
                // Focus the hotspot in the 360 editor.
                this.$refs.hotspotEditor360.focusHotspot(choice)
            }
            // Select the choice but don't scroll to it.
            this.selectChoice(choice, true, false)
        },
        // Open the editor sidebar.
        openEditor() {
            this.showEditor = true
        },
        // Close the node editor sidebar.
        closeEditor(event = null) {
            if (!event) {
                this.showEditor = false
                this.selectedChoiceIndex = null
                return
            }
            // Find out whether the click originated from an area where the editor should
            // stay open.
            let clickedInMenu = Boolean(event.target.closest("#editor-menu"))
            let clickedInHotspot = Boolean(event.target.closest(".draggable"))
            let createChoiceBtn = Boolean(event.target.closest("#create-choice"))

            if (this.showEditor && !clickedInMenu && !clickedInHotspot && !createChoiceBtn) {
                this.showEditor = false
                this.selectedChoiceIndex = null
            }
        },
        // Set the color of a choice (hotspot).
        setChoiceColor(choice, color) {
            this.$set(choice, "marker_color", color)
            if (this.contentIs360) {
                this.$refs.hotspotEditor360.updateHotspotColor(choice)
            }
        },
        // Set the shape of a choice (hotspot).
        setChoiceShape(choice, shape) {
            choice.marker_shape = shape
            if (shape == "rectangle") {
                choice.marker_height = choice.marker_radius
            }
        },
        setChoiceMarkerStyle(choice, style) {
            choice.marker_style = style
        },
        // Returns the color-specific class for a choice (only used for 2D hotspots).
        choiceClass(choice) {
            return `pulse-${choice.marker_color}`
        },
        // Returns an object used to style a choice (hotspot).
        choiceStyle(choice) {
            let styleObj = {
                width: choice.marker_radius + "px",
                height: choice.marker_shape == "circle" ? choice.marker_radius + "px" : choice.marker_height + "px",
            }
            if (choice.marker_style == "solid") {
                styleObj["background-color"] = this.markerColors[choice.marker_color]
            } else {
                styleObj["border"] = `solid 3px ${this.markerColors[choice.marker_color]}`
            }
            return styleObj
        },
        // Update the radius of a choice (propagate the change to the 3D editor).
        updateRadius(choice) {
            if (this.contentIs360) {
                this.$refs.hotspotEditor360.updateHotspotRadius(choice)
            }
        },
        // Update the angle of a 3D hotspot.
        updateAngle(choice) {
            this.$refs.hotspotEditor360.updateHotspotAngle(choice)
        },
        // Set the initial angle (alpha and beta euler angles) of the camera for 3D hotspots.
        setInitialCameraAngle() {
            let angles = this.$refs.hotspotEditor360.getCurrentCameraAngle()
            this.panel.init_camera_angle_alpha = angles.alpha
            this.panel.init_camera_angle_beta = angles.beta
        },
        // Convert an angle from radians to degrees.
        convertRadsToDegrees(angle) {
            return Math.round((10 * angle) / degreesToRadians) / 10
        },
        // Open the element list editor for a choice.
        openChoiceElementEditor(choice) {
            this.selectChoice(choice, false, false)
            $("#content-editor-modal").modal("show")
            this.modalLoading = true
            let vm = this
            http.get(`/api/choices/${choice.id}/`)
                .then(function (response) {
                    vm.selectedChoice = response.data
                    vm.selectedChoice.action = choice.action
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Save the elements associated with the selected choice.
        saveChoiceElements() {
            let formData = new FormData()

            // Clone the list of new elements.
            let clonedElements = JSON.parse(JSON.stringify(this.$refs.richContentEditor.elements))
            // Remove blank elements on each choice.
            clonedElements = Utilities.filterBlankElements(clonedElements)
            // Clean up element attributes.
            this.selectedChoice.elements.forEach((element, index) => {
                if (element.height === "") {
                    clonedElements[index].height = null
                }
                if (element.display_width === "") {
                    clonedElements[index].display_width = null
                }
            })

            // Append choice data as json.
            formData.append(
                "choice",
                JSON.stringify({
                    ...this.selectedChoice,
                    elements: clonedElements
                })
            )

            // Save the choice.
            this.modalLoading = true
            let vm = this
            http.put(`/api/choices/${this.selectedChoice.id}/`, formData)
                .then(function (response) {
                    $("#content-editor-modal").modal("hide")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        returnToEditor() {
            if (this.previousRoute && ["PreviewCaseSession", "CaseNodeMap"].includes(this.previousRoute.name)) {
                this.$router.push(this.previousRoute)
            } else {
                this.$router.push(this.returnURL)
            }
        },
        toggleEditorDisplay() {
            this.showEditor = !this.showEditor
        },
        moveChoicePosition(event, xIncrement, yIncrement) {
            let target = event.target
            // Keep the dragged position in the data-x/data-y attributes.
            var x = parseFloat(target.getAttribute("data-x")) || 0
            var y = parseFloat(target.getAttribute("data-y")) || 0

            // Move the choice more if the shift key was pressed (i.e. shift + arrow).
            let multiplier = event.shiftKey ? 3 : 1
            x += xIncrement * multiplier
            y += yIncrement * multiplier

            // Translate the element.
            target.style.transform = "translate(" + x + "px, " + y + "px)"

            // Update the posiion attributes.
            target.setAttribute("data-x", x)
            target.setAttribute("data-y", y)
        },
    },
    mounted() {
        let vm = this
        this.fetchData().then(() => {
            // Open the editor if at least one hotspot exists.
            if (vm.panel.choices.length > 0) {
                vm.showEditor = true
            }
        })

        // TODO: Revisit this re-positioning on window resize later. The image size stays
        // static across large screens and above, so this probably won't be a big issue.

        // // Add an event listener to re-position the hotspots on window resize.
        // let vm = this
        // let resize = throttle(function () {
        //     vm.initializeHotspotPositions()
        // }, 500)
        // window.addEventListener("resize", resize)

        // // Save the resize function so that we can remove the event listener on exit.
        // this.resizeWindowHandler = resize
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            // Store the previous route so that we know where to route the user
            // when they press the 'back to editor' button.
            vm.previousRoute = from
        })
    },
    beforeDestroy() {
        // window.removeEventListener("resize", this.resizeWindowHandler)
        // Removing this 'unset()' function - I was using this to destroy the event listeners
        // associated with interactjs elements, but this was causing issues when I enabled the
        // ability to resize elements.
        // interact(".draggable").unset()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

$hotspot-opacity: 0.915;

.hotspots-container-2d {
    width: 700px;
    @media (min-width: $breakpoint-3xl) {
        width: 760px;
    }
}

.hotspots-container-360 {
    width: 800px;
    @media (min-width: $breakpoint-3xl) {
        width: 900px;
    }
    @media (min-width: ($breakpoint-3xl + 200)) {
        width: 980px;
    }
}

.image-container {
    position: relative;
}

.spots-container {
    position: absolute;
}

.spot-selected {
    border: solid 2px $primary !important;
}

.draggable {
    margin: 0;
    color: #333;
    touch-action: none;
    user-select: none;
    transform: translate(0px, 0px);
    opacity: $hotspot-opacity;
    position: absolute;
}

.choice-focussed {
    border-color: $primary;
    border-width: 1px;
}

.active-marker {
    height: 20px;
    width: 20px;
}

.radius-range {
    max-width: 300px;
    min-width: 170px;
}

.radius-range-input {
    max-width: 66px;
}

.right-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1030;
    width: 510px;
    max-width: 100%;
    height: 100%;
    overflow-y: scroll;
    outline: 0;
}

.right-sidebar-content {
    margin-left: 0px;
    margin-top: 0px;
    margin-bottom: 0px;
    height: 100%;
}

.right-sidebar-content-block {
    padding: 18px;
}

.shape-dropdown {
    max-width: 4rem;
    min-width: 3rem !important;
}

.style-dropdown {
    max-width: 6rem;
    min-width: 5rem !important;
}

.transparent-spot {
    border: solid 1px;
    color: $gray;
}

.card-360 {
    overflow: hidden;
}

.btn-shape-picker {
    padding: 3px;
    margin-left: -6px;
}
</style>